import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { 
  MapPin, Search, Users, Edit, Plus, Briefcase, X,
  DollarSign, Clock, MoreVertical, CheckCircle, XCircle, RotateCcw 
} from 'lucide-react';

const Positions = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobScope, setJobScope] = useState('my'); // my | all

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      // Fetch ALL positions from all organizations (recruiters can see all positions)
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions/all`);
      
      if (response.data.success) {
        setPositions(response.data.data || []);
      } else {
        // Fallback to organization-specific endpoint
        const orgResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile?.organizationId}`
        );
        setPositions(orgResponse.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
      // Fallback to organization positions
      try {
        const orgResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile?.organizationId}`
        );
        setPositions(orgResponse.data?.data || []);
      } catch (err) {
        console.error('Fallback also failed:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredPositions = positions.filter(position => {
    const matchesSearch = position.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         position.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || position.status === statusFilter;

    const matchesScope = jobScope === 'all' || position.organization_id === userProfile?.organizationId;

    const locQuery = locationFilter.trim().toLowerCase();
    const matchesLocation = !locQuery || (position.location || '').toLowerCase().includes(locQuery);

    return matchesSearch && matchesStatus && matchesScope && matchesLocation;
  });

  const isExpired = (position) => {
    const publishedDate = new Date(position.published_at || position.created_at);
    const closesAt = position.closes_at ? new Date(position.closes_at) : new Date(publishedDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    return new Date() > closesAt;
  };

  const handleReopen = async (e, positionId) => {
    e.stopPropagation();
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/positions/${positionId}`, {
        status: 'published',
        closes_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
      fetchPositions();
    } catch (err) {
      console.error('Error reopening position:', err);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-gray-100 text-gray-700',
      closed: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700',
      filled: 'bg-blue-100 text-blue-700'
    };

    const icons = {
      published: <CheckCircle className="w-3 h-3" />,
      draft: <Clock className="w-3 h-3" />,
      closed: <XCircle className="w-3 h-3" />,
      expired: <Clock className="w-3 h-3" />,
      filled: <CheckCircle className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {icons[status]}
        <span className="capitalize">{status || 'draft'}</span>
      </span>
    );
  };

  return (
    <RecruiterLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Positions</h1>
            <p className="text-gray-600 mt-2">Manage your job postings</p>
          </div>
          <button
            onClick={() => navigate('/add-position')}
            className="mt-4 sm:mt-0 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Position
          </button>
        </div>

        {/* Search + Filters — matches My Candidates styling exactly */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-stretch bg-white rounded-full shadow-sm border-2 border-gray-900/90 focus-within:border-blue-500 transition-colors overflow-hidden">
            <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                data-testid="positions-search"
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear search">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setSearchTerm(''); setLocationFilter(''); setStatusFilter('all'); setJobScope('my'); }}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors"
              data-testid="positions-reset-filters"
            >
              Reset filters
            </button>
          </div>

          {/* Secondary filters (region + scope + status) */}
          <div className="flex flex-wrap gap-2 mt-3 px-1 items-center">
            <div className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-full px-3 py-1.5 focus-within:border-blue-500 transition-colors">
              <MapPin className="w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder="City, region or remote"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="bg-transparent outline-none text-xs text-gray-900 placeholder:text-gray-400 w-40"
                data-testid="positions-location-filter"
              />
              {locationFilter && (
                <button type="button" onClick={() => setLocationFilter('')} className="text-gray-400 hover:text-gray-600" aria-label="Clear location">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <select
              value={jobScope}
              onChange={(e) => setJobScope(e.target.value)}
              className="text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-full px-3 py-1.5 focus:outline-none focus:border-blue-500 cursor-pointer"
              data-testid="job-scope-filter"
            >
              <option value="my">My Jobs</option>
              <option value="all">All Jobs</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-full px-3 py-1.5 focus:outline-none focus:border-blue-500 cursor-pointer"
              data-testid="positions-status-filter"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="closed">Closed</option>
              <option value="filled">Filled</option>
            </select>
          </div>
        </div>

        {/* Positions List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading positions...</p>
          </div>
        ) : filteredPositions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No positions found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first position'}
            </p>
            <button
              onClick={() => navigate('/add-position')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add New Position
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPositions.map((position) => (
              <div
                key={position.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/positions/${position.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/positions/${position.id}`); } }}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid={`position-card-${position.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Left Section - Position Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                            {position.title}
                          </h3>
                          {getStatusBadge(isExpired(position) && position.status === 'published' ? 'expired' : position.status)}
                        </div>
                        <p className="text-gray-600 mb-2">
                          {position.recruiting_firm_name || position.organizations?.name || position.organization_name || position.company_name || 'Company Name'}
                        </p>
                      </div>
                    </div>

                    {/* Position Details */}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{position.location || 'Location not specified'}</span>
                      </div>
                      
                      {position.employment_type && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span className="capitalize">{position.employment_type}</span>
                        </div>
                      )}

                      {position.workplace && (
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          <span className="capitalize">{position.workplace}</span>
                        </div>
                      )}

                      {(position.salary_min || position.salary_max) && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          <span>
                            {position.salary_currency || '€'} {position.salary_min?.toLocaleString()}
                            {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Skills Preview */}
                    {position.must_have_skills && position.must_have_skills.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {position.must_have_skills.slice(0, 4).map((skill, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                        {position.must_have_skills.length > 4 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                            +{position.must_have_skills.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right Section - Stats & Actions */}
                  <div className="flex flex-col gap-3 lg:items-end">
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {/* Only show edit if position belongs to user's organization */}
                      {position.organization_id === userProfile?.organizationId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/positions/${position.id}/edit`); }}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                      {(position.status === 'closed' || isExpired(position)) && position.organization_id === userProfile?.organizationId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReopen(e, position.id); }}
                          className="bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2"
                          data-testid={`reopen-${position.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    Created {new Date(position.created_at).toLocaleDateString()}
                    {position.published_at && ` • Published ${new Date(position.published_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </RecruiterLayout>
  );
};

export default Positions;
